import ClientLogsService from "@src/services/client-logs.service";
import { getEnvironmentVariables } from "@src/services/environment-config.service";
import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface EnvironmentVariables {
  APP_ENV: string;
  FEATURE_PRESCRIBER: boolean;
  FEATURE_SCHEDULE_POLLING_STATUS: boolean;
  FEATURE_LABEL: boolean;
  FEATURE_UPDATED_REASON_FOR_VISIT: boolean;
  FEATURE_REASONS_FOR_VISIT_API: boolean;
  FEATURE_UPDATED_REFER_PAGE: boolean;
  FEATURE_PROVIDER_AVAILABILITY_BATCH: boolean;
  ENABLE_PATIENT_PORTAL_REGISTRATION: boolean;
  SCHEDULE_POLLING_INTERVAL_MS?: number;
  TIMER_INITIAL_VALUE?: number;
  IDLE_TIMEOUT?: number;
  COUNTDOWN_SECONDS?: number;
  PATIENT_NAVIGATOR_IDLE_TIMEOUT?: number;
  CONTACT_US_WEBFORM_URL: string;
}

interface EnvironmentContextValue extends EnvironmentVariables {
  isLoading: boolean;
}

const ENV_VAR_NAMES = [
  "APP_ENV",
  "FEATURE_PRESCRIBER",
  "FEATURE_SCHEDULE_POLLING_STATUS",
  "FEATURE_LABEL",
  "FEATURE_UPDATED_REASON_FOR_VISIT",
  "FEATURE_REASONS_FOR_VISIT_API",
  "FEATURE_UPDATED_REFER_PAGE",
  "FEATURE_PROVIDER_AVAILABILITY_BATCH",
  "ENABLE_PATIENT_PORTAL_REGISTRATION",
  "SCHEDULE_POLLING_INTERVAL_MS",
  "TIMER_INITIAL_VALUE",
  "IDLE_TIMEOUT",
  "COUNTDOWN_SECONDS",
  "PATIENT_NAVIGATOR_IDLE_TIMEOUT",
  "CONTACT_US_WEBFORM_URL",
] as const;

const defaultEnvironmentVariables: EnvironmentVariables = {
  APP_ENV: "",
  FEATURE_PRESCRIBER: false,
  FEATURE_SCHEDULE_POLLING_STATUS: false,
  FEATURE_LABEL: false,
  FEATURE_UPDATED_REASON_FOR_VISIT: false,
  FEATURE_REASONS_FOR_VISIT_API: false,
  FEATURE_UPDATED_REFER_PAGE: false,
  FEATURE_PROVIDER_AVAILABILITY_BATCH: false,
  ENABLE_PATIENT_PORTAL_REGISTRATION: false,
  SCHEDULE_POLLING_INTERVAL_MS: undefined,
  TIMER_INITIAL_VALUE: undefined,
  IDLE_TIMEOUT: undefined,
  COUNTDOWN_SECONDS: undefined,
  PATIENT_NAVIGATOR_IDLE_TIMEOUT: undefined,
  CONTACT_US_WEBFORM_URL: "",
};

const EnvironmentContext = createContext<EnvironmentContextValue | undefined>(undefined);

const parseBooleanEnv = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return false;
};

const parseStringEnv = (value: unknown): string => {
  if (typeof value === "string") return value;
  return "";
};

const parseNumberEnv = (value: unknown): number | undefined => {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

export const EnvironmentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [environment, setEnvironment] = useState<EnvironmentVariables>(defaultEnvironmentVariables);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadEnvironmentVariables = async () => {
      try {
        const batchResponse = await getEnvironmentVariables([...ENV_VAR_NAMES]);

        // Strict shape check: must be a plain object (not null, not array, not primitive)
        if (
          batchResponse &&
          typeof batchResponse === "object" &&
          !Array.isArray(batchResponse) &&
          !("error" in batchResponse)
        ) {
          const envResponse = batchResponse as Record<string, unknown>;

          setEnvironment({
            APP_ENV: parseStringEnv(envResponse.APP_ENV),
            FEATURE_PRESCRIBER: parseBooleanEnv(envResponse.FEATURE_PRESCRIBER),
            FEATURE_SCHEDULE_POLLING_STATUS: parseBooleanEnv(
              envResponse.FEATURE_SCHEDULE_POLLING_STATUS,
            ),
            FEATURE_LABEL: parseBooleanEnv(envResponse.FEATURE_LABEL),
            FEATURE_UPDATED_REASON_FOR_VISIT: parseBooleanEnv(
              envResponse.FEATURE_UPDATED_REASON_FOR_VISIT,
            ),
            FEATURE_REASONS_FOR_VISIT_API: parseBooleanEnv(
              envResponse.FEATURE_REASONS_FOR_VISIT_API,
            ),
            FEATURE_UPDATED_REFER_PAGE: parseBooleanEnv(envResponse.FEATURE_UPDATED_REFER_PAGE),
            FEATURE_PROVIDER_AVAILABILITY_BATCH: parseBooleanEnv(
              envResponse.FEATURE_PROVIDER_AVAILABILITY_BATCH,
            ),
            ENABLE_PATIENT_PORTAL_REGISTRATION: parseBooleanEnv(
              envResponse.ENABLE_PATIENT_PORTAL_REGISTRATION,
            ),
            SCHEDULE_POLLING_INTERVAL_MS: parseNumberEnv(envResponse.SCHEDULE_POLLING_INTERVAL_MS),
            TIMER_INITIAL_VALUE: parseNumberEnv(envResponse.TIMER_INITIAL_VALUE),
            IDLE_TIMEOUT: parseNumberEnv(envResponse.IDLE_TIMEOUT),
            COUNTDOWN_SECONDS: parseNumberEnv(envResponse.COUNTDOWN_SECONDS),
            PATIENT_NAVIGATOR_IDLE_TIMEOUT: parseNumberEnv(
              envResponse.PATIENT_NAVIGATOR_IDLE_TIMEOUT,
            ),
            CONTACT_US_WEBFORM_URL: parseStringEnv(envResponse.CONTACT_US_WEBFORM_URL),
          });
        } else if (batchResponse && typeof batchResponse === "object" && "error" in batchResponse) {
          // Explicit error response
          ClientLogsService.logError(
            "system",
            "EnvironmentContext.loadEnvironmentVariables",
            "Environment service returned error response",
            new Error((batchResponse as { error?: unknown }).error as string),
          );
        } else {
          // Unexpected response shape
          ClientLogsService.logError(
            "system",
            "EnvironmentContext.loadEnvironmentVariables",
            `Unexpected response shape from getEnvironmentVariables: ${typeof batchResponse}`,
            new Error(`Expected object, got ${typeof batchResponse}`),
          );
        }
      } catch (error) {
        // Log service failures for observability
        ClientLogsService.logError(
          "system",
          "EnvironmentContext.loadEnvironmentVariables",
          "Failed to load environment variables",
          error instanceof Error ? error : new Error(String(error)),
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadEnvironmentVariables();
  }, []);

  const contextValue = useMemo(() => ({ ...environment, isLoading }), [environment, isLoading]);

  return <EnvironmentContext.Provider value={contextValue}>{children}</EnvironmentContext.Provider>;
};

export const useEnvironment = (): EnvironmentContextValue => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error("useEnvironment must be used within an EnvironmentProvider");
  }
  return context;
};
